import { createContext, useContext } from 'react';
import type { CurrentUser, ModuleKey, Role, UserContext } from '@radiology/ui';
import { MODULE_KEYS } from '@radiology/ui';
import { primaryRole } from '../app/roles';

/**
 * The session shape the rest of the app codes against.
 *
 * Backed by `SessionProvider`, which loads `core.my_session` from Supabase.
 *
 * The Phase 2 stand-in (`DevSessionProvider`) that fabricated a session has been
 * deleted. Nothing outside this folder changed when it was swapped for the real
 * thing — every consumer uses `useSession()` and never touched the
 * implementation.
 */
export interface Session {
  user: CurrentUser;
  /** Canonical roles per module. A module absent here means no access. */
  rolesByModule: Partial<Record<ModuleKey, Role[]>>;
  context: UserContext;
  setActiveRole: (module: ModuleKey, role: Role) => void;
  setContext: (context: Partial<UserContext>) => void;
  signOut: () => void;
  isAuthenticated: boolean;
}

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a session provider');
  return ctx;
}

/** Modules the user holds at least one role in. */
export function accessibleModules(session: Session): ModuleKey[] {
  return MODULE_KEYS.filter(key => (session.rolesByModule[key]?.length ?? 0) > 0);
}

export function rolesIn(session: Session, module: ModuleKey): Role[] {
  return session.rolesByModule[module] ?? [];
}

/**
 * Active role for a module: whatever the user explicitly picked, otherwise the
 * highest-precedence role they hold there.
 */
export function activeRoleIn(session: Session, module: ModuleKey): Role | undefined {
  const roles = rolesIn(session, module);
  if (roles.length === 0) return undefined;
  const chosen = session.context.role;
  if (chosen && roles.includes(chosen)) return chosen;
  return primaryRole(roles);
}
